import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ExerciseModel } from '../../../models/exercise.model';
import { AcModel } from '../../../models/ac.model';
import { AcStatusSettingsExerciseType } from '../../../models/acstatus/ac-status.type';
import { CandidateModel } from '../../../models/candidate.model';
import { ObserverModel } from '../../../models/observer.model';
import { CdkDrag, CdkDragDrop, CdkDragEnter, CdkDragExit, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { v4 as uuidv4 } from 'uuid';
import { MenuItem, SelectItem } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { SINGLE_GROUP_UUID } from '../../../models/constants';

interface Stock {
  users: Array<ObservedCandidateModel | ObserverModel>;
}

class AssignedObserverModel extends ObserverModel {
  assigned = false;
}

class ObservedCandidateModel extends CandidateModel {
  observers: Array<AssignedObserverModel> | null = null;
}

interface Group {
  index: string;
  name?: string;
  moderator?: string | null;
  candidates: Array<ObservedCandidateModel>;
  observers: Array<ObserverModel>;
  moderatorOptions?: Array<SelectItem>;
  hoverObservers?: boolean;
  hoverCandidates?: boolean;
  inEdit?: boolean;
}

@Component({
  selector: 'app-exercise-settings-groups',
  templateUrl: './exercise-settings-groups.component.html',
  styleUrls: ['./exercise-settings-groups.component.scss']
})
export class ExerciseSettingsGroupsComponent implements OnInit, OnChanges {

  @Input() ac: AcModel = null;
  @Input() exercise: ExerciseModel = null;
  @Input() matrix = false;

  public status: AcStatusSettingsExerciseType | null;
  public candidates: Array<ObservedCandidateModel>;
  public observers: Array<ObserverModel>;
  public stock: Stock = {
    users: [],
  };
  public groups: Array<Group> = [];
  public hoverStock = false;

  public items: Array<MenuItem> = [];

  constructor(
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.ac && this.exercise && (changes.ac || changes.exercise)) {
      this.candidates = this.ac?.candidates.map((candidate: CandidateModel) => {
        return new ObservedCandidateModel().deserialize(candidate);
      });
      this.observers = [...this.ac?.observers];
      this.initFromStatus(this.ac.getExerciseStatus(this.exercise.id));
      this.initMenu();
    }
    if (changes.matrix) {
      this.makeAssignment();
    }
  }

  public notEnoughUsers(group: Group): string | null {
    let minCandidates = parseInt(this.exercise.group_settings?.min_candidates_per_team, 10);
    let minObservers = parseInt(this.exercise.group_settings?.min_observers_per_candidate, 10);
    if (isNaN(minCandidates)) {
      minCandidates = 0;
    }
    if (isNaN(minObservers)) {
      minObservers = 0;
    }
    if (group.candidates?.length < minCandidates) {
      // not enough candidates
      return this.transloco.translate('partials.exercise_settings.not_enough_candidates');
    } else {
      // not enough observers
      const message = this.transloco.translate('partials.exercise_settings.not_enough_observers');
      if (this.exercise.isTeam() && this.matrix === true) {
        let result = true;
        group.candidates?.forEach((candidate: ObservedCandidateModel) => {
          if (candidate.observers?.filter(observer => observer.assigned === true)?.length < minObservers) {
            result = false;
          }
        });
        return (!result) ? message : null;
      } else {
        return (group.observers?.length < minObservers) ? message : null;
      }
    }
  }

  // dropping

  public drop(event: CdkDragDrop<Array<any>>): void {
    if (event.previousContainer !== event.container) {
      const prevIndex = event.previousContainer.data.findIndex(item => item === event.item.data);
      transferArrayItem(event.previousContainer.data,
                        event.container.data,
                        prevIndex,
                        event.currentIndex);
    }
    this.buildModeratorOptions();
    this.makeAssignment();
    this.resetHoverMarkers();
    this.sortStaff();
  }

  public checkForObserver(drag: CdkDrag<ObserverModel>): boolean {
    return drag.data.role === 'observer' || drag.data.role === 'moderator';
  }

  public checkForCandidate(drag: CdkDrag<ObserverModel>): boolean {
    return drag.data.role === 'candidate';
  }

  public dropToCandidates(event: CdkDragDrop<Array<any>>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      const prevIndex = event.previousContainer.data.findIndex(item => item === event.item.data);
      transferArrayItem(event.previousContainer.data,
                        event.container.data,
                        prevIndex,
                        event.currentIndex);
    }
    this.buildModeratorOptions();
    this.makeAssignment();
    this.resetHoverMarkers();
  }

  public saveModerator(group: Group, event: any) {
    group.moderator = event.value;
  }

  public getPayload(): AcStatusSettingsExerciseType {
    if (this.ac?.isSingle()) {
      return this.ac.getSingleAcPseudoExerciseStatus();
    } else {
      const result: AcStatusSettingsExerciseType = {
        groups: {},
        candidates: {},
        observers: {},
      };
      this.stock.users.forEach((user: ObservedCandidateModel | ObserverModel) => {
        if (user.role === 'candidate') {
          result.candidates[user.hashed_id] = null;
        } else if (user.role === 'observer') {
          result.observers[user.hashed_id] = null;
        }
      });
      this.groups.forEach((group: Group) => {
        if (group.candidates.length > 0 || group.observers.length > 0) {
          result.groups[group.index] = {
            name: group.name || null,
            moderator: group.moderator || null
          };
          group.candidates.forEach((candidate: ObservedCandidateModel, index: number) => {
            const observers = candidate.observers
              .filter(observer => observer.assigned)
              .map(observer => observer.hashed_id);
            result.candidates[candidate.hashed_id] = {
              group: group.index,
              sequence: index,
              observers: (observers.length > 0) ? observers : null
            };
          });
          group.observers.forEach((oberserver: ObserverModel) => {
            result.observers[oberserver.hashed_id] = {
              group: group.index
            };
          });
        }
      });
      return result;
    }
  }

  public addGroup(): Group {
    const group: Group = {
      index: this.getHashForNewGroup(),
      name: this.getNameForNewGroup(),
      candidates: [],
      observers: [],
      hoverObservers: false,
      hoverCandidates: false,
      inEdit: false
    };
    this.groups.push(group);
    this.buildModeratorOptions();
    return group;
  }

  public deleteGroup(group: Group): void {
    while (group.candidates.length > 0) {
      this.moveUser(group.candidates[0], group.candidates, this.stock.users);
    }
    while (group.observers.length > 0) {
      this.moveUser(group.observers[0], group.observers, this.stock.users);
    }
    const index = this.groups.findIndex(actual => actual.index === group.index);
    if (index !== -1) {
      this.groups.splice(index, 1);
    }
  }

  public canAddGroup(): boolean {
    return this.ac?.isGroup() === true;
  }

  public canDeleteGroup(): boolean {
    return this.groups.length > 1;
  }

  private moveUser(user: ObservedCandidateModel | ObserverModel, source: Array<ObservedCandidateModel | ObserverModel>, target: Array<ObservedCandidateModel | ObserverModel>): void {
    const index = source.findIndex(actual => actual.hashed_id === user.hashed_id)
    if (index !== -1) {
      source.splice(index, 1).forEach(user => target.push(user));
    }
  }

  private initFromStatus(status: AcStatusSettingsExerciseType): void {
    let hasToBuildSuggestions = false;
    this.status = status;
    // init status
    if (!this.status) {
      this.status = {};
      hasToBuildSuggestions = true;
    }
    // init groups, candidates and observers
    if (
      !this.status.groups ||
      !this.status.candidates ||
      !this.status.observers
    ) {
      this.status.groups = {};
      this.status.candidates = {};
      this.status.observers = {};
      hasToBuildSuggestions = true;
    }
    if (hasToBuildSuggestions === true) {
      this.buildGroupSuggestions();
    } else {
      // sync groups
      this.groups = [];
      Object.keys(this.status.groups).forEach((index: string) => {
        this.groups.push({
          index,
          name: this.status.groups[index].name || this.getNameForNewGroup(),
          moderator: this.status.groups[index].moderator || null,
          candidates: [],
          observers: [],
        });
      });
      this.stock = {
        users: [],
      };
      // first: sync observers
      this.observers.forEach((observer: ObserverModel) => {
        this.stock.users.push(observer);
        if (
          this.status.observers[observer.hashed_id] &&
          this.status.observers[observer.hashed_id].group
        ) {
          const group = this.getGroupByIndex(this.status.observers[observer.hashed_id].group);
          if (group !== null) {
            this.moveUser(observer, this.stock.users, group.observers);
          }
        }
      });
      // second: sync candidates
      this.candidates.forEach((candidate: ObservedCandidateModel) => {
        this.stock.users.push(candidate);
        if (
          this.status.candidates[candidate.hashed_id] &&
          this.status.candidates[candidate.hashed_id].group
        ) {
          const group = this.getGroupByIndex(this.status.candidates[candidate.hashed_id].group);
          if (group !== null) {
            this.moveUser(candidate, this.stock.users, group.candidates);
            // assign observers
            if (Array.isArray(this.status.candidates[candidate.hashed_id].observers)) {
              candidate.observers = group.observers.map((observer: ObserverModel) => {
                return new AssignedObserverModel().deserialize({
                  ...observer,
                  assigned: this.status.candidates[candidate.hashed_id].observers.includes(observer.hashed_id)
                });
              });
            }
          }
        }
      });
      // add empty group
      if (this.groups.length === 0) {
        this.addGroup();
      }
    }
    this.buildModeratorOptions();
    this.makeAssignment();
  }

  public buildGroupSuggestions(): void {
    if (this.ac?.isSingle() === true) {
      this.initSingle();
    } else {
      if (this.exercise?.isTeam() === true) {
        this.initGroupTeam();
      } else {
        this.initGroupIndividual();
      }
    }
    this.buildModeratorOptions();
    this.makeAssignment();
  }

  public pullAllToStock(): void {
    this.stock = {
      users: [...this.candidates, ...this.observers],
    };
    this.groups = [];
    const group = this.addGroup();
    this.buildModeratorOptions();
    this.makeAssignment();
  }

  public putAllinOneGroup(): void {
    this.groups = [];
    const group = this.addGroup();
    group.candidates = this.candidates;
    group.observers = this.observers;
    this.stock = {
      users: []
    };
    this.buildModeratorOptions();
    this.makeAssignment();
  }

  private initSingle(): void {
    this.putAllinOneGroup();
  }

  private initGroupTeam(): void {
    const minCand = this.exercise?.getMinCandidatesPerTeam();
    const minObs = this.exercise?.getMinObserversPerCandidate();
    if (Array.isArray(this.candidates) && Array.isArray(this.observers) && minCand > 0 && minObs > 0) {
      if (!this.createGroupTeams([...this.candidates], [...this.observers], minCand, minObs)) {
        this.putAllinOneGroup();
      }
    }
  }

  private initGroupIndividual(): void {
    const minObs = this.exercise?.getMinObserversPerCandidate();
    if (Array.isArray(this.candidates) && Array.isArray(this.observers) && minObs > 0) {
      if (!this.createIndividualTeams([...this.candidates], [...this.observers], minObs)) {
        this.putAllinOneGroup();
      }
    }
  }

  private createGroupTeams(candidates: Array<ObservedCandidateModel>, observers: Array<ObserverModel>, minParticipantsA: number, minParticipantsB: number): boolean {
    this.groups = [];
    // Sicherstellen, dass genügend Teilnehmer für ein Team vorhanden sind
    if (candidates.length < minParticipantsA || observers.length < minParticipantsB) {
      return null;
    }
    while (candidates.length >= minParticipantsA && observers.length >= minParticipantsB) {
      const team: Group = {
        index: this.getHashForNewGroup(),
        name: this.getNameForNewGroup(),
        candidates: [],
        observers: [],
      };
      for (let i = 0; i < minParticipantsA; i++) {
        team.candidates.push(candidates.pop());
      }
      for (let i = 0; i < minParticipantsB; i++) {
        team.observers.push(observers.pop());
      }
      this.groups.push(team);
    }
    // Übrige Teilnehmer in Gruppe A einem bestehenden Team hinzufügen
    while (candidates.length > 0) {
      const randomTeamIndex = Math.floor(Math.random() * this.groups.length);
      this.groups[randomTeamIndex].candidates.push(candidates.pop());
    }
    // Übrige Teilnehmer in Gruppe B einem bestehenden Team hinzufügen
    while (observers.length > 0) {
      const randomTeamIndex = Math.floor(Math.random() * this.groups.length);
      this.groups[randomTeamIndex].observers.push(observers.pop());
    }
    this.stock.users = [];
    return this.groups.length > 0;
  }

  private createIndividualTeams(candidates: Array<ObservedCandidateModel>, observers: Array<ObserverModel>, minObservers: number): boolean {
    this.groups = [];
    if (candidates.length > 0 && observers.length > 0 && minObservers > 0) {
      while (observers.length >= minObservers * 2) {
        const team: Group = {
          index: this.getHashForNewGroup(),
          name: this.getNameForNewGroup(),
          candidates: [],
          observers: observers.splice(0, minObservers),
        };
        this.groups.push(team);
      }
      if (observers.length >= minObservers) {
        const team: Group = {
          index: this.getHashForNewGroup(),
          name: this.getNameForNewGroup(),
          candidates: [],
          observers,
        };
        this.groups.push(team);
      }
      let count = 0;
      while (candidates.length > 0) {
        this.groups[count].candidates = [...this.groups[count].candidates, ...candidates.splice(0, 1)];
        if (count >= this.groups.length - 1) {
          count = 0;
        } else {
          count++;
        }
      }
    }
    this.stock.users = [];
    return this.groups.length > 0;
  }

  private buildModeratorOptions(): void {
    this.groups.forEach((group: Group) => {
      const options = [];
      let actualFound = false;
      // main moderator
      options.push({
        value: null,
        label: this.ac?.moderator?.fullname
      });
      // observers as group moderators
      group.observers.forEach((observer: ObserverModel) => {
        const item = {
          value: observer.hashed_id,
          label: observer.fullname
        };
        options.push(item);
        if (observer.hashed_id === group.moderator) {
          actualFound = true;
          group.moderator = item.value;
        }
      });
      group.moderatorOptions = options;
      if (!actualFound) {
        group.moderator = null;
      }
    });
  }

  public assignObserver(candidate: ObservedCandidateModel, observer: AssignedObserverModel): void {
    const found = candidate.observers?.find(actual => actual.hashed_id === observer.hashed_id) || null;
    if (found !== null) {
      found.assigned = !found.assigned;
    }
  }

  private makeAssignment(): void {
    this.candidates.forEach((candidate: ObservedCandidateModel) => {
      if (this.matrix === true) {
        const group = this.groups.find(group => group.candidates.find(actual => actual.hashed_id === candidate.hashed_id) !== undefined) || null;
        if (group !== null) {
          candidate.observers = group.observers.map((observer: ObserverModel) => {
            const oldObserver = candidate.observers?.find(actual => actual.hashed_id === observer.hashed_id) || null;
            return new AssignedObserverModel().deserialize({
              ...observer,
              assigned: (oldObserver !== null) ? oldObserver.assigned : true
            });
          });
        } else {
          this.deleteAssignedObservers(candidate);
        }
      } else {
        this.deleteAssignedObservers(candidate);
      }
    });
  }

  private deleteAssignedObservers(candidate: ObservedCandidateModel): void {
    candidate.observers = [];
  }

  private getGroupByIndex(index: string): Group | null {
    return this.groups.find(group => group.index === index) || null;
  }

  private getNameForNewGroup(): string {
    return this.transloco.translate('partials.exercise_settings.new_group') + ' ' +  (this.groups.length + 1);
  }

  private getHashForNewGroup(): string {
    return this.ac?.isGroup() ? uuidv4() : SINGLE_GROUP_UUID;
  }

  private resetHoverMarkers(): void {
    this.hoverStock = false;
    this.groups.forEach((group: Group) => {
      group.hoverObservers = false;
      group.hoverCandidates = false;
    });
  }

  private sortStaff(): void {
    // stock
    const roleOrder = ['candidate', 'observer', 'moderator'];
    this.stock.users.sort((userA: ObservedCandidateModel | ObserverModel, userB: ObservedCandidateModel | ObserverModel) => {
      let valueA: any = userA.initials;
      let valueB: any = userB.initials;
      if (userA.role !== userB.role) {
        valueA = roleOrder.indexOf(userA.role);
        valueB = roleOrder.indexOf(userB.role);
      }
      return (valueA > valueB) ? 1 : ((valueB > valueA) ? -1 : 0);
    });
    // observers
    this.groups.forEach((group: Group) => {
      group.observers.sort((observerA: ObserverModel, observerB: ObserverModel) => {
        return (observerA.initials > observerB.initials) ? 1 : ((observerB.initials > observerA.initials) ? -1 : 0);
      });
    });
  }

  // menu

  private initMenu(): void {
    this.items = [];
    if (this.ac?.isGroup()) {
      this.items = [
        {
          label: this.transloco.translate('partials.exercise_settings.suggest'),
          icon: 'pi pi-sitemap',
          command: () => {
            this.buildGroupSuggestions();
          }
        },
        {
          label: this.transloco.translate('partials.exercise_settings.all_in_one'),
          icon: 'pi pi-clone',
          command: () => {
            this.putAllinOneGroup();
          }
        },
        {
          label: this.transloco.translate('partials.exercise_settings.all_in_stock'),
          icon: 'pi pi-times',
          command: () => {
            this.pullAllToStock();
          }
        },
      ];
    }
  }
}
